import { recoverStaleSurveySession } from '@chiroup/components/body/recoverStaleSurveySession';
import { Button, ButtonColors } from '@chiroup/components/Button';
import { titleCase } from '@chiroup/core/functions/titleCase';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { reportRequestSubmitError } from '../../services/reportError';
import requestService from '../../services/request.service';

// Ported from healthcom-app's UploadInsuranceSide. The only material
// change is that `primaryColor` now comes from props (no MeContext
// in healthcom-schedule).

type Props = {
  requestId: string;
  primaryColor: string;
  validationCode: string | number;
  prMap: { [key: string]: string };
  side: 'front' | 'back';
  imagePreview: string | null;
  onChangeImagePreview: (url: string | null) => void;
  className?: string;
  onUploadInsuranceCard: (url: string) => void;
  language?: string;
  onUploadStart?: () => void;
};

const UploadInsuranceSide: React.FC<Props> = ({
  requestId,
  primaryColor,
  validationCode,
  prMap,
  side,
  onChangeImagePreview,
  imagePreview,
  onUploadInsuranceCard,
  className,
  language = 'en',
  onUploadStart,
}) => {
  const [uploading, setUploading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const previewUrlRef = useRef<string | null>(null);

  useEffect(() => {
    return () => {
      if (previewUrlRef.current) {
        URL.revokeObjectURL(previewUrlRef.current);
      }
    };
  }, []);

  const onDrop = useCallback(
    async (acceptedFiles: File[]) => {
      if (!acceptedFiles?.length) return;
      setError(null);

      if (previewUrlRef.current) {
        URL.revokeObjectURL(previewUrlRef.current);
      }
      const urlObject = URL.createObjectURL(acceptedFiles[0]);
      previewUrlRef.current = urlObject;
      onChangeImagePreview(urlObject);

      setUploading(true);
      onUploadStart?.();

      try {
        const res = await requestService.uploadInsuranceCard(
          requestId,
          validationCode,
          acceptedFiles[0],
          side,
          prMap,
          'insurance'
        );

        onUploadInsuranceCard?.(res);
      } catch (err) {
        if (recoverStaleSurveySession({ err, language })) return;

        reportRequestSubmitError(err, { step: `insuranceCard:${side}` });
        onChangeImagePreview(null);
        setError(
          language === 'es'
            ? 'No se pudo subir la imagen. Por favor, intente de nuevo.'
            : 'Upload failed. Please try again.'
        );
      } finally {
        setUploading(false);
      }
    },
    [
      language,
      onUploadInsuranceCard,
      prMap,
      requestId,
      onChangeImagePreview,
      side,
      validationCode,
      onUploadStart,
    ]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    multiple: false,
    maxSize: 10485760,
    accept: {
      'image/jpeg': ['.jpg', '.jpeg'],
      'image/png': ['.png'],
      'image/gif': ['.gif'],
    } as any,
  });

  return (
    <div className={className || ''}>
      <div className="flex flex-col justify-center sm:min-h-screen">
        <div className="grow">
          <div className="px-2 py-2 bg-white dark:bg-dark-gray-700 border-b border-gray-300 dark:border-dark-gray-500 rounded-t-md sm:px-6">
            <div className="flex flex-wrap items-center justify-between w-full -mt-2 -ml-4 sm:flex-nowrap">
              <div className="flex items-center mt-2 ml-4">
                <h4 className="block text-sm font-medium leading-5 text-gray-900 sm:mt-px sm:pt-2 dark:text-dark-gray-200">
                  {language === 'es'
                    ? 'Tarjeta de Seguro - ' +
                      `${side === 'front' ? 'Parte Frontal' : 'Parte Posterior'}`
                    : `Insurance Card - ${titleCase(side)}`}
                </h4>
              </div>
              {imagePreview && (
                <Button
                  text="Try again"
                  className="pt-4"
                  color={ButtonColors.plain}
                  onClick={() => onChangeImagePreview(null)}
                />
              )}
            </div>
          </div>
          <div
            {...getRootProps()}
            className="flex justify-center max-w-lg mx-2 mt-4 border-2 border-dashed rounded-md"
            style={{ borderColor: isDragActive ? primaryColor : '#d1d5db' }}
          >
            {imagePreview ? (
              <div className="relative w-full rounded-md">
                <img className="w-full rounded-md" src={imagePreview} alt="ID" />
                {uploading && (
                  <div
                    className="absolute top-0 left-0 flex items-center justify-center w-full h-full rounded-md opacity-75"
                    style={{ backgroundColor: primaryColor }}
                  >
                    <svg
                      className="w-20 h-20 mr-3 -ml-1 text-white animate-spin"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                  </div>
                )}
              </div>
            ) : (
              <div className="px-6 pt-5 pb-6">
                <input {...getInputProps()} />
                <div className="text-center">
                  <svg
                    className="w-12 h-12 mx-auto text-gray-400"
                    stroke="currentColor"
                    fill="none"
                    viewBox="0 0 48 48"
                  >
                    <path
                      d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                  <p className="mt-1 text-sm text-gray-600">
                    <button
                      type="button"
                      className="font-medium transition duration-150 ease-in-out hover:opacity-90 focus:outline-none focus:underline"
                      style={{ color: primaryColor }}
                    >
                      Upload a file
                    </button>{' '}
                    or drag and drop
                  </p>
                  <p className="mt-1 text-xs text-gray-500">PNG, JPG, GIF, PDF up to 10MB</p>
                </div>
              </div>
            )}
          </div>
          {error && <p className="mt-2 text-sm text-red-600 text-center">{error}</p>}
        </div>
      </div>
    </div>
  );
};

export default UploadInsuranceSide;
